import PropTypes from "prop-types"
import React, { useEffect, useState, useMemo } from "react"
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Badge,
} from "reactstrap"

import { useParams, useNavigate, Link } from "react-router-dom"

import Breadcrumbs from "../../components/Common/Breadcrumb2"
import TableContainer from "../../components/Common/TableContainer"
import { withTranslation } from "react-i18next"
import { get } from "helpers/api_helper"
import "../../components/Common/datatables.scss"
import Spinners from "components/Common/Spinner"
import moment from "moment"

const ActionBadge = ({ action }) => {
  switch (action) {
    case "create":
      return <Badge className="bg-success">Create</Badge>
    case "update":
      return <Badge className="bg-warning">Update</Badge>
    case "delete":
      return <Badge className="bg-danger">Delete</Badge>
    default:
      return <Badge className="bg-secondary">{action}</Badge>
  }
}

const LineSettingHistory = props => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [logs, setLogs] = useState([])
  const [logsLoading, setLogsLoading] = useState(true)

  document.title = "History | " + process.env.REACT_APP_CMS_TITLE

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const result = await get(
          process.env.REACT_APP_BACKEND_URL + `/v1/admin/line-oa/${id}/logs`
        )
        setLogs(result.data || [])
      } catch (err) {
        console.error("Fetch logs failed:", err)
        setLogs([])
      } finally {
        setLogsLoading(false)
      }
    }
    fetchLogs()
  }, [id])

  const columns = useMemo(
    () => [
      {
        header: "#",
        enableColumnFilter: false,
        enableSorting: false,
        cell: cellProps => (
          <div style={{ display: "flex", justifyContent: "center" }}>
            {parseInt(cellProps.row.id) + 1}
          </div>
        ),
      },
      {
        header: "Action",
        accessorKey: "action",
        enableColumnFilter: false,
        enableSorting: true,
        cell: cellProps => (
          <ActionBadge action={cellProps.row.original.action} />
        ),
      },
      {
        header: "Changed Fields",
        accessorKey: "changed_fields",
        enableColumnFilter: false,
        enableSorting: false,
        cell: cellProps => {
          const fields = cellProps.row.original.changed_fields || []
          return fields.map((f, i) => (
            <Badge key={i} className="bg-info me-1">{f}</Badge>
          ))
        },
      },
      {
        header: "Old Values",
        accessorKey: "old_values",
        enableColumnFilter: false,
        enableSorting: false,
        cell: cellProps => {
          const vals = cellProps.row.original.old_values
          if (!vals) return "-"
          return (
            <div style={{ maxWidth: "250px", wordBreak: "break-word" }}>
              {Object.entries(vals).map(([key, val]) => (
                <div key={key}>
                  <strong>{key}:</strong> {String(val ?? "-")}
                </div>
              ))}
            </div>
          )
        },
      },
      {
        header: "New Values",
        accessorKey: "new_values",
        enableColumnFilter: false,
        enableSorting: false,
        cell: cellProps => {
          const vals = cellProps.row.original.new_values
          if (!vals) return "-"
          return (
            <div style={{ maxWidth: "250px", wordBreak: "break-word" }}>
              {Object.entries(vals).map(([key, val]) => (
                <div key={key}>
                  <strong>{key}:</strong> {String(val ?? "-")}
                </div>
              ))}
            </div>
          )
        },
      },
      {
        header: "Changed By",
        accessorKey: "changed_by_name",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Date",
        accessorKey: "created_at",
        enableColumnFilter: false,
        enableSorting: true,
        cell: cellProps => (
          moment(cellProps.row.original.created_at).format("YYYY-MM-DD HH:mm:ss")
        ),
      },
    ],
    []
  )

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            title={props.t("History")}
            breadcrumbItems={[
              {
                title: props.t("System Setting"),
                link: props.t("systemsettings"),
              },
              {
                title: props.t("Line Account Setting"),
                link: props.t("linesetting"),
              },
              {
                title: props.t("History"),
                link: props.t(`history/${id}`),
              },
            ]}
          />

          <div className="bInnerContent">
            {logsLoading ? (
              <Spinners setLoading={setLogsLoading} />
            ) : (
              <>
                <TableContainer
                  isAddButton={false}
                  isExportButton={false}
                  columns={columns}
                  data={logs || []}
                  isGlobalFilter={true}
                  isPagination={true}
                  SearchPlaceholder="Search"
                  pagination="pagination"
                  paginationWrapper="dataTables_paginate paging_simple_numbers"
                  tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                  isCustomPageSize={true}
                />
                <div className="mt-3">
                  <Link
                    className="btn btn-secondary"
                    to="/systemsettings/linesetting"
                  >
                    <i className="bx bx-arrow-back me-1"></i>
                    Back
                  </Link>
                </div>
              </>
            )}
          </div>
        </Container>
      </div>
    </React.Fragment>
  )
}

LineSettingHistory.propTypes = {
  t: PropTypes.any,
}

export default withTranslation()(LineSettingHistory)
